import React, { useState, useEffect } from 'react';
import { Formik, FieldArray, Form, Field } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { createCourseSchema } from '../../../../forms/createCourseSchema';
import courseService from '../../../../services/mentor/courseService';
import FormField from '../../../../components/common/FormField';
import Loader from '../../../../components/common/Loader';
import { showToast } from '../../../../utils/showToast';
import type { ICourse, ICourseModule, IMaterial } from '../../../../types/course';
import { Plus, X } from 'lucide-react';

// Helper function to safely get nested errors
const getNestedError = (errors: any, touched: any, path: string): string => {
  const keys = path.split('.');
  let currentError: any = errors;
  let currentTouched: any = touched;

  for (const key of keys) {
    if (!currentError || !currentTouched) break;
    currentError = currentError[key];
    currentTouched = currentTouched[key];
  }

  return currentTouched && typeof currentError === 'string' ? currentError : '';
};

interface CourseFormValues {
  title: string;
  description: string;
  price: number;
  duration: string;
  image: string;
  published: boolean;
  modules: ICourseModule[];
}

const CourseUpload: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [currentCourse, setCurrentCourse] = useState<ICourse | null>(null);
  const [allCourses, setAllCourses] = useState<ICourse[]>([]);
  const [isLoadingCourse, setIsLoadingCourse] = useState(!!id);

  useEffect(() => {
    const fetchAllCoursesForDropdown = async () => {
      try {
        const coursesData = await courseService.getAllCoursesForAdmin();
        setAllCourses(coursesData);
      } catch (err: any) {
        showToast(err.message, { type: 'error' });
      }
    };
    fetchAllCoursesForDropdown();
  }, []);

  useEffect(() => {
    if (id) {
      const fetchCourseData = async () => {
        setIsLoadingCourse(true);
        try {
          const data = await courseService.getCourseForEdit(id);
          setCurrentCourse(data);
        } catch (err: any) {
          showToast(err.message, { type: 'error' });
          navigate('/dashboard/mentor/manage-courses');
        } finally {
          setIsLoadingCourse(false);
        }
      };
      fetchCourseData();
    }
  }, [id, navigate]);

  if (isLoadingCourse) {
    return <div className="flex min-h-64 items-center justify-center"><Loader /></div>;
  }

  return (
    <div className="py-10 px-4">
      <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6 text-center">
          {id ? 'Edit Course' : 'Create New Course'}
        </h1>

        {!id && (
          <div className="mb-6">
            <label htmlFor="courseSelect" className="block text-sm font-medium">
              Or, select an existing course to edit:
            </label>
            <select
              id="courseSelect"
              onChange={(e) => e.target.value && navigate(`/dashboard/mentor/manage-courses/upload/${e.target.value}`)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">-- Create a New Course --</option>
              {allCourses.map(course => (
                <option key={course._id} value={course._id}>{course.title}</option>
              ))}
            </select>
          </div>
        )}

        <Formik<CourseFormValues>
          initialValues={{
            title: currentCourse?.title || '',
            description: currentCourse?.description || '',
            price: currentCourse?.price || 0,
            duration: currentCourse?.duration || '',
            image: currentCourse?.image || '',
            published: currentCourse?.published || false,
            modules: currentCourse?.modules || [],
          }}
          validationSchema={createCourseSchema}
          enableReinitialize={true}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            try {
              setSubmitting(true);
              const payload = { ...values, price: Number(values.price) };
              if (id) {
                await courseService.updateCourse(id, payload);
                showToast('Course updated successfully!', { type: 'success' });
              } else {
                await courseService.createCourse(payload);
                showToast('Course created successfully!', { type: 'success' });
              }
              resetForm();
              navigate('/dashboard/mentor/manage-courses');
            } catch (err: any) {
              showToast(err.message, { type: 'error' });
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
            <Form className="space-y-6">
              <FormField
                label="Course Title"
                name="title"
                type="text"
                value={values.title}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.title && errors.title ? errors.title : ''}
              />

              <div className="flex flex-col space-y-1">
                <label htmlFor="description" className="text-sm font-medium text-gray-700">Course Description</label>
                <Field
                  as="textarea"
                  id="description"
                  name="description"
                  rows={5}
                  className={`w-full px-4 py-2 border rounded-lg ${touched.description && errors.description ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {touched.description && errors.description && (<span className="text-xs text-red-600">{errors.description}</span>)}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Price (₹)"
                  name="price"
                  type="number"
                  value={values.price.toString()}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.price && errors.price ? errors.price : ''}
                />
                <FormField
                  label="Duration (e.g., '2.5 months')"
                  name="duration"
                  type="text"
                  value={values.duration}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.duration && errors.duration ? errors.duration : ''}
                />
              </div>

              <FormField
                label="Course Image URL (optional)"
                name="image"
                type="text"
                value={values.image}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.image && errors.image ? errors.image : ''}
                placeholder="e.g., https://example.com/course-image.jpg"
              />

              <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4 border-t pt-6">Course Modules</h2>
              <FieldArray name="modules">
                {({ push, remove }) => (
                  <div className="space-y-4">
                    {values.modules.map((mod, modIdx) => (
                      <div key={modIdx} className="p-4 border rounded-lg space-y-4 bg-gray-50">
                        <div className="flex justify-between items-center">
                          <h3 className="text-md font-semibold">Module {modIdx + 1}</h3>
                          <button type="button" onClick={() => remove(modIdx)} className="text-red-500 hover:text-red-700"><X size={18} /></button>
                        </div>
                        <FormField
                            label="Module Title"
                            name={`modules.${modIdx}.title`}
                            type="text"
                            value={mod.title}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={getNestedError(errors, touched, `modules.${modIdx}.title`)}
                        />
                        <FormField
                            label="Module Description"
                            name={`modules.${modIdx}.description`}
                            type="text"
                            value={mod.description}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={getNestedError(errors, touched, `modules.${modIdx}.description`)}
                        />

                        <FieldArray name={`modules.${modIdx}.materials`}>
                          {({ push: pushMat, remove: removeMat }) => (
                            <div className="pl-4 border-l-2 border-gray-200 space-y-3">
                              {mod.materials?.map((mat: IMaterial, matIdx: number) => (
                                <div key={matIdx} className="border rounded p-3 bg-white">
                                  <div className="flex justify-between items-center mb-2">
                                    <h4 className="text-sm font-medium">Material {matIdx + 1}</h4>
                                    <button type="button" onClick={() => removeMat(matIdx)} className="text-red-500 hover:text-red-700"><X size={16} /></button>
                                  </div>
                                  <FormField
                                    label="Material Name"
                                    name={`modules.${modIdx}.materials.${matIdx}.name`}
                                    type="text"
                                    value={mat.name}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={getNestedError(errors, touched, `modules.${modIdx}.materials.${matIdx}.name`)}
                                  />
                                  <FormField
                                    label="Material URL"
                                    name={`modules.${modIdx}.materials.${matIdx}.url`}
                                    type="text"
                                    value={mat.url}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={getNestedError(errors, touched, `modules.${modIdx}.materials.${matIdx}.url`)}
                                  />
                                  <div className="flex flex-col space-y-1">
                                    <label htmlFor={`modules.${modIdx}.materials.${matIdx}.type`} className="text-sm">Type</label>
                                    <Field as="select" id={`modules.${modIdx}.materials.${matIdx}.type`} name={`modules.${modIdx}.materials.${matIdx}.type`} className="w-full px-2 py-1 border rounded">
                                      <option value="">Select Type</option>
                                      <option value="pdf">PDF</option>
                                      <option value="document">Document</option>
                                      <option value="video">Video</option>
                                      <option value="other">Other</option>
                                    </Field>
                                    <span className="text-xs text-red-600">{getNestedError(errors, touched, `modules.${modIdx}.materials.${matIdx}.type`)}</span>
                                  </div>
                                </div>
                              ))}
                              <button type="button" onClick={() => pushMat({ name: '', url: '', type: 'pdf' })} className="text-sm text-blue-600 hover:text-blue-800 font-medium"><Plus size={14} className="inline" /> Add Material</button>
                            </div>
                          )}
                        </FieldArray>
                      </div>
                    ))}
                    <button type="button" onClick={() => push({ title: '', description: '', materials: [] })} className="bg-blue-500 text-white px-4 py-2 rounded text-sm mt-4 hover:bg-blue-600 font-medium"><Plus size={16} className="inline mr-1" /> Add Module</button>
                  </div>
                )}
              </FieldArray>

              <div className="flex items-center space-x-2 mt-8 border-t pt-6">
                <Field id="published" type="checkbox" name="published" className="h-4 w-4 rounded" />
                <label htmlFor="published" className="text-sm font-medium">Publish this course upon saving</label>
              </div>

              <button type="submit" disabled={isSubmitting} className="w-full bg-blue-600 text-white font-semibold py-3 rounded-lg flex items-center justify-center space-x-2 disabled:opacity-50 hover:bg-blue-700">
                {isSubmitting ? <Loader /> : <span>{id ? 'Update Course' : 'Create Course'}</span>}
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default CourseUpload;
